/**
 * Rate limiting middleware with subscription tier support
 * @package @app/api
 */

import { Ratelimit } from '@upstash/ratelimit';
import { Redis } from '@upstash/redis';

/**
 * Subscription tier rate limits
 * Requests per minute by plan name
 */
export const TIER_LIMITS = {
  // Free tier (no subscription)
  free: { requests: 10, window: '60 s' as const },
  // Basic plan
  basic: { requests: 60, window: '60 s' as const },
  // Pro plan
  pro: { requests: 300, window: '60 s' as const },
  // Enterprise plan
  enterprise: { requests: 1000, window: '60 s' as const },
  // Admin users (unlimited-ish)
  admin: { requests: 10000, window: '60 s' as const },
} as const;

export type SubscriptionTier = keyof typeof TIER_LIMITS;

/**
 * Get subscription tier from plan name.
 * Agent and university plans all get at least 'pro' tier since they're paying customers.
 *
 * Unknown / unrecognized plan strings default to 'free' (the most restrictive
 * tier) — defaulting to a higher tier on corrupt or unexpected data would
 * silently grant elevated rate limits.
 */
export function getTierFromPlan(plan: string | null | undefined): SubscriptionTier {
  if (!plan) return 'free';

  const p = plan.toLowerCase();

  if (p.includes('enterprise')) return 'enterprise';
  if (p === 'pro' || p.startsWith('agent-') || p.startsWith('university-')) return 'pro';
  if (p === 'free') return 'free';

  // Unknown plan — fail closed at the most restrictive tier.
  return 'free';
}

/**
 * Create rate limiters for each tier
 * Uses sliding window algorithm for smooth rate limiting
 */
export function createTierRateLimiters() {
  // Skip if no Redis URL configured
  if (!process.env.UPSTASH_REDIS_REST_URL || !process.env.UPSTASH_REDIS_REST_TOKEN) {
    if (process.env.NODE_ENV === 'production') {
      console.error(
        'CRITICAL: Rate limiting disabled in production — UPSTASH_REDIS_REST_URL or UPSTASH_REDIS_REST_TOKEN not configured',
      );
    } else {
      console.warn(
        'Rate limiting disabled: UPSTASH_REDIS_REST_URL or UPSTASH_REDIS_REST_TOKEN not configured',
      );
    }
    return null;
  }

  const redis = Redis.fromEnv();

  const limiters = {} as Record<SubscriptionTier, Ratelimit>;

  for (const [tier, config] of Object.entries(TIER_LIMITS)) {
    limiters[tier as SubscriptionTier] = new Ratelimit({
      redis,
      limiter: Ratelimit.slidingWindow(config.requests, config.window),
      prefix: `matrix:ratelimit:${tier}:`,
      analytics: true,
    });
  }

  return limiters;
}

/**
 * Rate limit result with tier info
 */
export interface RateLimitResult {
  success: boolean;
  limit: number;
  remaining: number;
  reset: number;
  tier: SubscriptionTier;
}

/**
 * Check rate limit for a user based on their subscription tier
 */
export async function checkRateLimit(
  limiters: Record<SubscriptionTier, Ratelimit> | null,
  userId: string,
  tier: SubscriptionTier,
): Promise<RateLimitResult> {
  // If rate limiting is disabled, allow all requests
  if (!limiters) {
    return {
      success: true,
      limit: TIER_LIMITS[tier].requests,
      remaining: TIER_LIMITS[tier].requests,
      reset: Date.now() + 60000,
      tier,
    };
  }

  const limiter = limiters[tier];
  const result = await limiter.limit(userId);

  return {
    success: result.success,
    limit: result.limit,
    remaining: result.remaining,
    reset: result.reset,
    tier,
  };
}

/**
 * Rate limit headers for HTTP responses
 */
export function getRateLimitHeaders(result: RateLimitResult): Record<string, string> {
  return {
    'X-RateLimit-Limit': String(result.limit),
    'X-RateLimit-Remaining': String(result.remaining),
    'X-RateLimit-Reset': String(result.reset),
    'X-RateLimit-Tier': result.tier,
    ...(result.success
      ? {}
      : {
          'Retry-After': String(Math.ceil((result.reset - Date.now()) / 1000)),
        }),
  };
}

/**
 * Rate limit error class
 */
export class RateLimitError extends Error {
  public readonly result: RateLimitResult;
  public readonly statusCode = 429;

  constructor(result: RateLimitResult) {
    super(
      `Rate limit exceeded. Retry after ${Math.ceil((result.reset - Date.now()) / 1000)} seconds.`,
    );
    this.name = 'RateLimitError';
    this.result = result;
  }
}
